export interface FrontendMenuSettings {
    show_admin?: boolean;
    show_team?: boolean;
    show_branding?: boolean;
    [key: string]: boolean | undefined;
}

export interface FrontendFeatureSettings {
    sharing?: boolean;
    smart_templates?: boolean;
    template_editing?: boolean;
    template_suggestions?: boolean;
    search_recordings?: boolean;
    [key: string]: boolean | undefined;
}

export interface FrontendSettings {
    version?: number;
    features?: FrontendFeatureSettings;
    menu?: FrontendMenuSettings;
}

export interface UserCapabilities {
    can_share?: boolean;
    is_admin?: boolean;
    is_superadmin?: boolean;
    is_org_admin?: boolean;
    // Organisatie-instellingen; ontbreken bij een oudere backend en tellen dan als true.
    can_download_transcript?: boolean;
    can_download_audio?: boolean;
    can_play_audio?: boolean;
    [key: string]: boolean | undefined;
}

export interface User {
    id: string;
    username: string;
    level: string;
    email?: string;
    canShare?: boolean;
    user_type?: string;
    is_admin?: boolean;
    is_org_admin?: boolean;
    organization_id?: number;
    group_id?: number;
    capabilities?: UserCapabilities;
    frontend_settings?: FrontendSettings;
}

export interface Tag {
    id: number;
    name: string;
    description?: string;
    color: string;
    file_count?: number;
    created_at?: string;
    updated_at?: string;
}

export type SharedPermissionLevel = 'view' | 'edit' | 'download' | 'manage' | 'owner';

export interface Recording {
    id: string;
    name: string;
    status: ProcessingStatus;
    duration: string;
    recordingDateTime?: string;
    createdAt: string;
    updatedAt: string;
    audioUrl?: string;
    tags?: Tag[];
    canEdit?: boolean;
    canDownload?: boolean;
    canManage?: boolean;
    sharedPermission?: SharedPermissionLevel | null;
}

export type RecordingSearchStatus = 'ready' | 'building';

export type RecordingSearchSort = 'relevance' | 'date_desc' | 'date_asc';

export type RecordingSearchMatchKind = 'title' | 'transcript' | 'summary' | 'supporting_document' | 'tag';

export interface RecordingSearchMatch {
    kind: RecordingSearchMatchKind;
    // Plain text met alleen <mark>…</mark>-markering; nooit als ruwe HTML renderen
    snippet: string;
    // BM25-score, lager = relevanter; null bij title-/tagmatches via metadata
    score: number | null;
    // Alleen aanwezig bij kind === 'summary'; bruikbaar om direct het juiste verslag te openen
    meetingType?: string;
}

export interface RecordingSearchResult {
    recording: Recording;
    matches: RecordingSearchMatch[];
}

export interface RecordingSearchResponse {
    status: RecordingSearchStatus;
    totalCount: number;
    results: RecordingSearchResult[];
}

export enum ProcessingStatus {
    NOT_UPLOADED = 'NOT_UPLOADED',
    UPLOADING = 'UPLOADING',
    NOT_STARTED = 'NOT_STARTED',
    NOT_PROCESSED = 'NOT_PROCESSED',
    QUEUED = 'QUEUED',
    PROCESSING = 'PROCESSING',
    PROCESSED = 'PROCESSED',
    ERROR = 'Error',
}

export interface AdditionalSettings {
    goal: string;
    perspective: string;
    audience: string;
    style: string;
    tense: string;
    structure: string;
    // Verslaglengte, canonieke backend-sleutel "lengte".
    // Leeg = automatisch; verder "beknopt"|"normaal"|"uitgebreid", "400", "600-800", "15%".
    length: string;
}

export interface MeetingType {
    id: number;
    // Sleutel voor de template-endpoints (/template/{ref}/…). Ontbreekt zolang
    // de backend nog geen uuid meestuurt; gebruik dan getTemplateRef().
    uuid?: string;
    // Sleutel voor de verslag-endpoints (meeting_type). Bij persoonlijke
    // templates lijkt dit ook op een uuid, maar het is níet de template-uuid.
    name: string;
    title: string;
    description: string;
    type: string;
    is_default: boolean;
    is_org_wide: boolean;
    content?: string; // Template content (markdown)
    smartTemplateSchema?: SmartTemplateSchema | null;
    additionalSettings?: AdditionalSettings | null;
}

export interface SmartTemplateField {
    code: string;
    label: string;
    description?: string;
    required: boolean;
    multiline: boolean;
    exportKey?: string;
}

export interface SmartTemplateSchema {
    version: number;
    syntax: 'field_markers';
    fields: SmartTemplateField[];
}

export interface ExtractedFieldItem {
    code: string;
    label: string;
    value: string | string[] | null;
    description?: string;
    required: boolean;
    multiline: boolean;
    exportKey?: string;
}

export interface TemplatePreview {
    content: string;
    title?: string;
    description?: string;
    additionalSettings?: any; // JSON string of object met AI-gegenereerde settings
    smartTemplateSchema?: SmartTemplateSchema | null;
}

// Door de backend herkende lengtelimieten per sectie (uit [placeholder]-annotaties in de templatetekst)
export interface SectionLengthLimit {
    section: string;
    description: string;
    unit: string;
    minValue: number | null;
    maxValue: number | null;
    maxWordsEquivalent: number | null;
}

export interface SectionLengthLimits {
    limits: SectionLengthLimit[];
    sectionsTotal: number;
    sectionsWithLimit: number;
    fullyAnnotated: boolean;
    totalMaxWords: number | null;
}

export type TemplateDraftStatus = 'draft' | 'applied' | 'snapshot';

export interface TemplateDraft {
    id: number;
    templateId?: number;
    // Het template waar dit concept echt bij hoort; kan afwijken van de uuid in
    // het pad (persoonlijke kopie van een standaardtemplate).
    templateUuid?: string;
    revisionNumber: number;
    title?: string;
    description?: string;
    revisionReason?: string;
    status: TemplateDraftStatus;
    createdAt: string;
    createdBy?: string;
    content?: string;
    smartTemplateSchema?: SmartTemplateSchema | null;
    additionalSettings?: AdditionalSettings | null;
    sectionLengthLimits?: SectionLengthLimits | null;
}

export type TemplateComponentMode = 'preview' | 'content' | 'metadata' | 'system' | 'task' | 'auto';
export type TemplateComponent = 'system_message' | 'task' | 'content' | 'title' | 'description';

export type TemplateSuggestionStatus = 'open' | 'accepted' | 'applied' | 'rejected' | 'failed';

export interface TemplateSuggestion {
    id: number;
    template_id: number;
    template_name?: string;
    template_title?: string;
    template?: { id: number; uuid?: string; name: string; title: string; description?: string } | null;
    status: TemplateSuggestionStatus;
    original_feedback: string;
    normalized_instruction: string;
    target_components: TemplateComponent[];
    confidence?: number;
    reasoning?: string;
    resolution_note?: string;
    created_at: string;
    updated_at: string;
}

export interface SupportingDocumentSummary {
    id: number;
    filename: string;
    content_type: string;
    description?: string;
    file_progress_id: number;
}

export interface Summary {
    id: string;
    fileId: string;
    // Naam van het template waaronder het verslag is opgeslagen; altijd bruikbaar.
    meetingType: string;
    // Sleutel voor de verslag-endpoints. null bij oudere verslagen of een
    // verwijderd template, undefined als de backend hem (nog) niet meestuurt.
    templateUuid?: string | null;
    content: string;
    sourceContent?: string;
    title: string;
    createdAt: string;
    updatedAt: string;
    additionalRecordings?: string[];
    templateRevisionId?: number;
    supportingDocuments?: SupportingDocumentSummary[];
    extractedFields?: Record<string, unknown> | null;
    extractedFieldItems?: ExtractedFieldItem[];
    extractionErrors?: string[];
    templateSchema?: SmartTemplateSchema | null;
}

// Wat een verslag-endpoint nodig heeft: de template-uuid als die bekend is, en
// de naam als fallback (oudere verslagen, verwijderd template, oude backend).
export type SummaryRef = Pick<Summary, 'meetingType' | 'templateUuid'>;

export interface AuthTokens {
    accessToken: string;
    refreshToken: string;
    expiresAt: Date;
}

export interface ApiResponse<T = any> {
    data?: T;
    error?: string;
    status: number;
}

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: Date;
    canAddToSummary?: boolean;
    addedToSummary?: boolean;
    status?: 'sending' | 'delivered' | 'error';
    isAddingToSummary?: boolean;
}

export interface TranscriptLine {
    speaker: string;
    text: string;
    timestamp: number;
}

export interface SupportingDocument {
    id: number;
    filename: string;
    content_type: string;
    description?: string;
    file_size: number;
    created_at: string;
    content_length: number;
    content?: string; // Voor volledige content wanneer opgehaald
}

export type FileShareTargetType = 'user' | 'group';
export type FileSharePermission = 'view' | 'edit' | 'download' | 'manage';

export interface FileShare {
    id: number;
    targetType: FileShareTargetType;
    targetUserUuid?: string | null;
    targetGroupId?: number | null;
    targetId?: number | null;
    permission: FileSharePermission;
    expiresAt?: string | null;
    note?: string | null;
    status?: string;
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
    revokedAt?: string | null;
    targetName?: string;
    targetEmail?: string;
    targetIdentifier?: string;
    revokedBy?: number | string | null;
}

export interface CreateFileShareInput {
    targetType: FileShareTargetType;
    targetUserUuid?: string;
    targetGroupId?: number;
    permission?: FileSharePermission;
    expiresAt?: string | null;
    note?: string | null;
}

export interface GroupMember {
    userUuid: string;
    username: string;
    email?: string;
}

export interface TeamGroupInfo {
    id: number;
    name: string;
}

export interface TeamGroupUsersResponse {
    group: TeamGroupInfo;
    members: GroupMember[];
}

export interface UserPreferences {
    personalContext?: string;
    phoneNumber?: string;
    authMethod: 'email' | 'sms';
    retentionDays?: number | null;
    organizationRetentionDays?: number | null;
    effectiveRetentionDays?: number | null;
    /** Eigen bewaartermijn voor verslagen; null = volgt retentionDays.
     *  Staat los van retentionDays: de verslagtermijn wist alleen de verslagen,
     *  de opname en het transcript blijven tot de opnametermijn. */
    summaryRetentionDays?: number | null;
    organizationSummaryRetentionDays?: number | null;
    effectiveSummaryRetentionDays?: number | null;
}

/**
 * Productupdates / changelog. Voor nu lokaal gemockt (zie updatesService),
 * later serverside opgehaald — daarom bewust een serialiseerbaar, plat model.
 */
export type UpdateCategory = 'nieuw' | 'verbeterd' | 'aankondiging';

/**
 * Publicatiekanaal. Een update kan op meerdere kanalen tegelijk verschijnen.
 * Deze web-app toont alleen updates die 'web' bevatten (zie updatesService).
 */
export type UpdateChannel = 'web' | 'app' | 'desktop';

export interface UpdateBlock {
    type: 'heading' | 'paragraph' | 'list';
    /** Voor heading & paragraph */
    text?: string;
    /** Voor list */
    items?: string[];
}

// --- Exporteren via /export/v2 ---
// De server stuurt de wizard stap voor stap aan; de client kent alleen soorten
// stappen, veldtypes en acties, geen platformen. Teksten van de server altijd
// als platte tekst tonen. Onbekende sleutels negeren.

export type ExportFlowCapability =
    | 'step.form' | 'step.pick' | 'step.pick.browse' | 'step.confirm' | 'step.message' | 'step.web'
    | 'field.text' | 'field.date_range' | 'field.select' | 'field.toggle'
    | 'action.open_url' | 'action.web' | 'action.sso_relogin';

export interface ExportFlowAction {
    type: 'open_url' | 'web' | 'sso_relogin';
    label: string;
    /** Alleen bij open_url; openen als hij met https:// begint */
    url?: string;
}

export type ExportDestinationStateKind = 'ready' | 'needs_link' | 'needs_reconnect' | 'web_only' | 'unavailable';

export interface ExportDestinationState {
    kind: ExportDestinationStateKind;
    message?: string;
    action?: ExportFlowAction;
}

export interface ExportDestination {
    config_id: number;
    name: string;
    /** icon_url is relatief t.o.v. de backend */
    platform: { key: string; label: string; icon_url: string | null };
    state: ExportDestinationState;
}

export interface ExportDestinationsResponse {
    destinations: ExportDestination[];
    last_used_config_id: number | null;
}

export interface ExportTextField {
    key: string;
    type: 'text';
    label: string;
    placeholder?: string;
    min_length?: number;
    max_length?: number;
    required?: boolean;
    default?: string;
}

export type ExportDateRangePreset = 'today' | 'week' | 'month';

export interface ExportDateRangeValue {
    /** YYYY-MM-DD */
    start: string;
    end: string;
}

export interface ExportDateRangeField {
    key: string;
    type: 'date_range';
    label: string;
    required?: boolean;
    default: ExportDateRangeValue;
    max_days?: number;
    presets?: ExportDateRangePreset[];
}

export interface ExportSelectField {
    key: string;
    type: 'select';
    label: string;
    options: { value: string; label: string }[];
    required?: boolean;
    default?: string;
}

export interface ExportToggleField {
    key: string;
    type: 'toggle';
    label: string;
    default?: boolean;
}

export type ExportField = ExportTextField | ExportDateRangeField | ExportSelectField | ExportToggleField;

/** Tekst als string, date_range als {start, end}, select als de value, toggle als boolean */
export type ExportFieldValue = string | boolean | ExportDateRangeValue;

export interface ExportResultItem {
    /** Ondoorzichtig: nooit tonen, ongewijzigd terugsturen */
    token: string;
    title: string;
    subtitle: string | null;
    detail: string | null;
    url: string | null;
    /** false: grijs tonen, met unavailable_reason */
    selectable: boolean;
    unavailable_reason: string | null;
    preselected: boolean;
    /** Alleen bij step.pick.browse */
    has_children: boolean;
}

export interface ExportFormStep {
    kind: 'form';
    title: string;
    fields: ExportField[];
    button: string;
}

export interface ExportPickStep {
    kind: 'pick';
    title: string;
    search: { fields: ExportField[]; mode: 'on_submit' | 'as_you_type'; debounce_ms?: number } | null;
    /** null: nog niet gezocht */
    results: ExportResultItem[] | null;
    more: boolean;
    /** De lijst is afgekapt: vraag om preciezer te zoeken */
    truncated: boolean;
    notice: string | null;
    empty_text: string | null;
    create: { label: string; field: ExportField } | null;
}

export interface ExportConfirmStep {
    kind: 'confirm';
    title: string;
    message: string | null;
    style: 'default' | 'warning';
    button: string;
    fields: ExportField[];
    /** true: meteen versturen, de keuze was de bevestiging */
    auto_submit: boolean;
}

export interface ExportMessageStep {
    kind: 'message';
    title: string | null;
    message: string;
    action: ExportFlowAction | null;
}

export interface ExportWebStep {
    kind: 'web';
    message: string;
    button: string;
}

export type ExportStep = ExportFormStep | ExportPickStep | ExportConfirmStep | ExportMessageStep | ExportWebStep;

export interface ExportStepResponse {
    /** Ondoorzichtig, ongewijzigd terugsturen */
    state: string;
    step: ExportStep;
}

export type ExportStepEvent =
    | { type: 'search'; inputs: Record<string, ExportFieldValue> }
    | { type: 'select'; token: string }
    | { type: 'create'; inputs: Record<string, ExportFieldValue> }
    | { type: 'submit_form'; inputs: Record<string, ExportFieldValue> }
    | { type: 'more' }
    | { type: 'open'; token: string };

export interface ExportSubmitResult {
    success: true;
    display: { message: string; url: string | null };
    replayed?: boolean;
}

export interface ExportFlowErrorBody {
    success: false;
    error: string;
    error_code: string;
    field?: string;
    retry_safe?: boolean | null;
    action?: ExportFlowAction;
}

export interface ExportAttemptStatus {
    status: 'pending' | 'success' | 'failed' | 'unknown';
    retry_safe: boolean;
    result?: ExportSubmitResult | ExportFlowErrorBody;
}

export interface ExportWebSessionCreated {
    session_id: string;
    url: string;
    expires_at: string;
}

export interface ExportWebSessionStatus {
    status: 'pending' | 'done' | 'cancelled' | 'expired';
    result?: ExportSubmitResult;
}

// --- Partnerkoppelingen (/integrations) ---

export type IntegrationLinkKind = 'organization' | 'user';

/** Waar de klik bij de partner vandaan kwam, bv. een afspraak */
export interface IntegrationLinkContext {
    type?: string;
    ref?: string;
    label?: string;
    startsAt?: string;
}

export interface OpenIntegrationLinkResponse {
    kind: IntegrationLinkKind;
    partnerName: string;
    claim: string;
    expiresAt: string;
}

export interface IntegrationLinkDetails {
    kind: IntegrationLinkKind;
    partnerName: string;
    tenantName: string | null;
    /** Naam van de medewerker bij de partner (kind=user) */
    displayName: string | null;
    context: IntegrationLinkContext | null;
    expiresAt: string;
    /** Wie er nu ingelogd is */
    account: { username: string; email: string };
    organizationName: string | null;
    canConfirm: boolean;
    alreadyLinked: boolean;
    reason: string | null;
    /** Nederlandse uitleg als canConfirm=false; letterlijk tonen */
    message: string | null;
    configId?: number;
    /** kind=user: het account hing aan een andere medewerker */
    replacesLink?: { displayName: string | null };
    /** kind=organization: alleen een nieuwe sleutel */
    replacesExisting?: boolean;
    /** kind=organization: verhuist uit een andere organisatie of van een ander account */
    movesFromOtherOrganization?: boolean;
    /** kind=organization: 'account' = Notizy-account zonder organisatie (zelfstandige praktijk) */
    owner?: IntegrationPracticeOwner;
    /** kind=organization: vinkje "ook mijn account" tonen (de partner stuurde mee wie er klikte) */
    includeUserOffered?: boolean;
    /** kind=organization: beginstand van dat vinkje */
    includeUserDefault?: boolean;
}

export type IntegrationPracticeOwner = 'organization' | 'account';

export type IntegrationLinkStatus = 'linked' | 'rotated' | 'moved' | 'already_linked' | 'replaced';

export interface ConfirmIntegrationLinkResponse {
    kind: IntegrationLinkKind;
    status: IntegrationLinkStatus;
    configId: number;
    linkId?: number;
    /** kind=organization */
    owner?: IntegrationPracticeOwner;
    /** kind=organization: ook het eigen account gekoppeld (vinkje); anders null */
    userLink?: { status: IntegrationLinkStatus; linkId: number; displayName: string | null } | null;
    context: IntegrationLinkContext | null;
    partnerName: string;
    tenantName: string | null;
}

/** Praktijkkoppeling op het eigen account (zelfstandige praktijk zonder organisatie) */
export interface IntegrationPracticeLink {
    configId: number;
    name: string;
    partnerName: string | null;
    tenantName: string | null;
    isActive: boolean;
    linkedSince: string;
    userLink: { id: number; displayName: string | null } | null;
}

/** Medewerkerkoppeling van de ingelogde gebruiker (Mijn koppelingen) */
export interface IntegrationUserLink {
    id: number;
    configId: number;
    configName: string | null;
    partnerName: string | null;
    tenantName: string | null;
    displayName: string | null;
    linkedAt: string;
}

/**
 * Eén onderwijsmedewerker uit de Edu-V-referentiedata (voor de
 * koppel-dropdown). Edu-V kent hier geen primaire ketenidentifier, dus het
 * id-type hoort bij de waarde en moet meelopen naar employee_user_id_type.
 */
export interface EduVEmployee {
    /** Waarde voor employee_user_id; letterlijk doorgeven, niet tonen */
    id: string;
    /** NEPPI | BPI | eduID | ASI | eckId */
    idType?: string;
    name: string;
    /** Extra onderscheidende velden, nodig bij naamgenoten */
    details?: string;
}

/**
 * Per-gebruiker overrides op een (organisatiebrede) exportconfiguratie.
 * Voor Praktijkdata is dat de eigen medewerker-koppeling; de backend gebruikt
 * die als standaardfilter bij zoeken en bij DOCUMENT-exports.
 */
export interface ExportUserSettings {
    configId: string;
    targetPlatform: string;
    /** Alleen deze velden mogen via PUT gezet worden */
    overridableFields: string[];
    settings: Record<string, unknown>;
}

/** Eén medewerker uit de Praktijkdata-referentiedata (voor de koppel-dropdown) */
export interface PraktijkdataMedewerker {
    id: number;
    name: string;
}

export interface Update {
    id: string;
    /** URL-vriendelijke sleutel, gebruikt in /updates/:slug */
    slug: string;
    title: string;
    summary: string;
    /** Machine-sleutel van de categorie; bepaalt kleur/CSS-class. */
    category: UpdateCategory;
    /**
     * Door de server gelokaliseerd, toonbaar label voor de categorie
     * (bijv. "Announcement"). Additief: valt terug op het eigen label
     * uit CATEGORY_META wanneer de feed dit veld (nog) niet levert.
     */
    categoryLabel?: string;
    /** Kanalen waarop deze update verschijnt; minimaal één. */
    channels: UpdateChannel[];
    /** ISO-datum van publicatie */
    publishedAt: string;
    /** Uitgelicht bericht op de frontpage (max. één tegelijk) */
    featured?: boolean;
    version?: string;
    /** Rijke inhoud voor de detailpagina */
    body: UpdateBlock[];
}
